import { FormProvider } from '@formily/react';
import { Form } from '@formily/antd-v5';
import { createForm, type IFormProps } from '@formily/core';
import { Button, Space } from 'antd';
import { useMemo, useRef } from 'react';

import type { IFormilySchema } from '../../types';
import { findFormComponent } from '../../utils/findFormComponent';
import { injectRequiredValidators } from '../../utils/injectRequiredValidators';
import { requiredIgnoringDeletedEffect } from '../../utils/formEffects';
import { parseJSONSchema } from '../../utils/data-parser';
import { usePage } from '../../context/PageContext';
import { useTranslate } from '../../../translate';
import xReactionParser from '../../utils/xReactionParser';
import { escapeFormItemsSchemas } from '../../utils';
import createSchemaFields from '../../utils/createSchemaFields';
import { stripSkipOnSubmitFields } from '../../utils/stripSkipOnSubmitFields';

interface IProps {
  schema: IFormilySchema;
  cancelButtonText?: string;
  saveButtonText?: string;
  parentContext: Record<string, any>;
  isSubmitting?: boolean;
  onSubmit: (values: Record<string, unknown>) => void;
  onCancel: () => void;
}

const ActionForm: React.FC<IProps> = ({
  schema,
  cancelButtonText = 'Cancel',
  saveButtonText = 'Save',
  onSubmit,
  onCancel,
  parentContext,
  isSubmitting,
}) => {
  const pageData = usePage();
  const { t } = useTranslate();

  const FormSchemaField = useRef(createSchemaFields()).current;
  const form = useMemo(
    () =>
      createForm({
        context: pageData.context,
        effects: requiredIgnoringDeletedEffect,
      } as IFormProps<any>),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const formSchema = useMemo(() => findFormComponent(schema), [schema]);

  const parsedSchema = useMemo(() => {
    if (formSchema)
      return parseJSONSchema(
        formSchema,
        {
          ...parentContext,
          ...pageData.context,
        },
        { t }
      );
    return formSchema;
  }, [schema, pageData.context, t]);

  const processedSchema = useMemo(
    () =>
      escapeFormItemsSchemas(xReactionParser(parsedSchema as IFormilySchema)),
    [parsedSchema]
  );

  const handleSubmit = async () => {
    try {
      await form.submit();
      const values = stripSkipOnSubmitFields(form);
      onSubmit(values);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  if (!processedSchema) {
    return <div>Invalid schema: Form component not found</div>;
  }

  // TODO: check this one
  const schemaWithValidators = injectRequiredValidators(processedSchema);

  return (
    <FormProvider form={form}>
      <Form form={form} layout="vertical">
        <FormSchemaField schema={schemaWithValidators as any} />
        <Space
          style={{ marginTop: 16, width: '100%', justifyContent: 'flex-end' }}
        >
          <Button onClick={onCancel}>{cancelButtonText}</Button>
          <Button type="primary" loading={isSubmitting} onClick={handleSubmit}>
            {saveButtonText}
          </Button>
        </Space>
      </Form>
    </FormProvider>
  );
};

export default ActionForm;
